import { all, call, debounce, delay, fork, put, race, select, take, takeLatest, takeLeading, } from '@redux-saga/core/effects';
import { filter, find, findIndex, get, isNil, some } from 'lodash';
import { captureException, captureMessage } from '@sentry/react';
import { DateTime } from 'luxon';
import { anchorSectionTimer, codeSoloSyncAppStateFlushed, executeCode, executeCodeFailure, faceAuthStateUpdate, fetchCombinedReport, fetchIvyReportDetails, fetchLanguageSpeakingReport, fetchNextQuestionById, fetchQuestionsList, fetchTcfMeasure, fetchTestInstance, formSubmissionStateUpdate, getAppState, getStepDetails, initialize, resetCombinedReport, resetExecuteCodeLoading, resetSectionState, saveAnswer, saveAppState, saveBulkAnswer, setCodeSoloStatus, setOnboardingWaitInfo, setShouldRedirectToStatusError, setTestInstanceId, setUpdatedParsedMaxSectionTime, startAssessment, startAssessmentError, startSection, stopSection, stopSectionSettled, storeAnswer, storeAppState, storeAssessment, storeBulkAnswer, storeCombinedReport, storeExecuteCodeResult, storeIvyReportDetails, storeLanguageReport, storeNextQuestion, storeQuestionList, storeSection, storeStepDetails, storeStopSection, storeTcfMeasurement, storeWorkflowCandidateStepId, lookUpWorkflowCandidateStepId, storeWorkflowCandidateStepIdLookup, submitSection, subscribeExecuteCode, syncAppState, triggerTimeUp, updateAssessmentPreformRequest, setSelfBookRoute, updatePreInstructionState, updateTotalAssessmentTime, } from '@containers/Assessment/slice';
import { catchError } from '@utils/sentry';
import postData from '@utils/postData';
import { CANX_GET_APP_STATE, CANX_UPSERT_APP_STATE, CREATE_TEST_INSTANCE, CR_EXECUTE_CODE, GET_ALL_QUESTION, GET_ASSESSMENT_REPORT_DETAILS, GET_NEXT_QUESTION_BY_ID, GET_TCF_MEASUREMENT, GET_WORKFLOW_CANDIDATE_STEP_ID_BY_TEST_INSTANCE, GET_WORKFLOW_CANDIDATE_STEP_ID, SAVE_ANSWERS, START_ASSESSMENT, START_SECTION, STOP_SECTION, SUBSCRIBE_CODE_EXECUTION, WFT_UPDATE_WORKFLOW_CANDIDATE_STEP_SYNC, } from '@containers/Assessment/queries';
import { assessmentType, findActionWindow, getCentralizeSectionTime, getLatestTestInstanceId, getMatchedWorkflowCandidateStep, getNextQuestionId, getOverallSections, getStepDurationPercentage, hasResultLabel, parseBulkSavedAnswer, parseSubmittedAnswer, postMessageToChild, processStartAssessmentResponse, processStartSectionResponse, syncAppState as syncAppStateMessage, } from '@containers/Assessment/helpers';
import { TCF_MEASURE_MAX_ATTEMPTS, TCF_MEASURE_RETRY_DELAY_MS } from '@containers/Assessment/assessment.constants';
import { extendWindowEnd, getWindowConstrainedTime } from '@containers/Assessment/timerHelpers';
import { selectAllQuestion, selectAnswers, selectAppState, selectAssessment, selectAssessmentSection, selectCurrentQuestion, selectCurrentSectionTimer, selectNextQuestionId, selectParsedMaxSectionTime, selectPreInstructionState, selectQuestionIds, selectStartAssessmentResponse, selectStartSectionResponse, selectStepDetail, selectTestInstanceId, selectWcsId, } from '@containers/Assessment/selectors';
import { proviewSessionInitialized, resetProviewSession, setProviewSessionInitStatus, setShouldStartProview, } from '../Proview/slice';
import { selectProviewInitStatus, selectProviewSessionUUID } from '../Proview/selectors';
import { getCurrentDate } from '@utils/dateHelpers';
import { resetProviewSDKState } from '@utils/proviewSDK';
import fetchData from '@utils/fetchData';
import { storeQueryParams } from '@containers/Auth/slice';
import { bulkAnswerSubmitted, candidateViewExtendedDuration, cxAsyncAssessmentResumeAttempted, cxAsyncAssessmentResumeFailed, cxSelfBookRouteResolved, cxStepWindowActionAllowed, cxStepWindowApiError, cxStepWindowEarlyJoinBlocked, cxStepWindowLateOnboardingAllowed, cxStepWindowNoRestrictionFlow, cxStepWindowOnboardingAllowed, cxStepWindowOnboardingFallbackToAction, } from '@utils/mixpanel/mixpanelActions';
import { stopHeartBeat } from '@containers/App/types';
import { growthBookFeatureFlags } from '@utils/featureFlags';
import { growthBook } from '@utils/growthBook';
import { APP_STATE_PAYLOAD, ASSESSMENT_PRE_REGISTRATION_FORM, PROVIEW_SESSION_UUID, localStorageGetItem, localStorageSetItem, removeLocalStorageItem, } from '@utils/localStorageHelpers';
import { catchException } from '@utils/catchException';
import { shouldIncludeDriveOccurrences } from '@containers/App/helpers';
import { GET_STEP_DETAILS, GET_STEP_DRIVE_OCCURRENCES } from '@containers/App/queries';
import { SELF_BOOK_ROUTE, findBookedGuest, getStepGuests, resolveSelfBookRoute, } from '@containers/Assessment/SelfBook/utils';
import { selectCustomToken, selectQueryParams } from '@containers/Auth/selectors';
import { ensureSecureBrowser, isSecureBrowserRequiredForStep } from '@containers/Assessment/secureBrowserGate';
import { V8, codeRunnerStatus } from '@containers/App/app.constant';
import { formatExecuteCodeSubscriptionOutput, formatIvyReportDetails, formatLanguageEvaluationReport, normalizeCombinedReportResponse, } from '@containers/Assessment/assessment.helper';
import { createFaceAuthSuccess } from '@containers/BiometricAuthentication/FaceAuth/slice';
import { createApolloSubscriptionChannel } from '@utils/apolloEvent';
const STATUS_ERROR_STATUSES = ['SUSPENDED', 'TURNED_DOWN', 'HOLD', 'SELECTED'];
const UNAUTHORIZED_START_ASSESSMENT_TOKENS = ['user not authorized', 'not authorized', 'unauthorized'];
const UNAUTHORIZED_START_ASSESSMENT_UI_MESSAGE_KEY = 'app.components.TestTaker.ErrorScreens.AssessmentErrorView.unauthorizedUser';
const actionAllowedTrackedSessionKeys = new Set();
const hasUnauthorizedStartAssessmentToken = (messages) => messages.some((message) => typeof message === 'string' &&
    UNAUTHORIZED_START_ASSESSMENT_TOKENS.some((token) => message.toLowerCase().includes(token)));
const INVITE_EXPIRED_TOKENS = ['invite has expired', 'invite had expired', 'invite expired'];
const INVITE_EXPIRED_UI_MESSAGE_KEY = 'app.components.TestTaker.ErrorScreens.AssessmentErrorView.inviteExpired';
const ASSESSMENT_ALREADY_COMPLETED_UI_MESSAGE_KEY = 'app.components.TestTaker.ErrorScreens.AssessmentErrorView.assessmentAlreadyCompleted';
const hasInviteExpiredToken = (messages) => messages.some((message) => typeof message === 'string' && INVITE_EXPIRED_TOKENS.some((token) => message.toLowerCase().includes(token)));
export function* getConstrainedProcessedSectionData(sectionData, appState) {
    const stepForWindow = (yield select(selectStepDetail));
    const processedData = (yield call(processStartSectionResponse, sectionData, appState, stepForWindow));
    const durationPercentage = getStepDurationPercentage(stepForWindow);
    const constrainedProcessedData = {
        ...processedData,
        parsedMaxSectionTime: getWindowConstrainedTime(processedData.parsedMaxSectionTime, findActionWindow(stepForWindow), durationPercentage),
    };
    return constrainedProcessedData;
}
function* waitForProctoringWindow(targetWindow, windowType, actionVariant = null) {
    if (!targetWindow?.starts_at) {
        return false;
    }
    const windowStart = DateTime.fromISO(targetWindow.starts_at);
    if (!windowStart.isValid) {
        return false;
    }
    const now = DateTime.fromMillis(Date.now());
    if (now >= windowStart) {
        return false;
    }
    const waitDuration = Math.max(0, Math.floor(windowStart.diff(now).as('milliseconds')));
    if (waitDuration <= 0) {
        return false;
    }
    yield put(setOnboardingWaitInfo({
        shouldShow: true,
        startsAt: targetWindow.starts_at,
        expiredAt: null,
        windowType,
        actionVariant,
    }));
    yield delay(waitDuration);
    yield put(setOnboardingWaitInfo({
        shouldShow: false,
        startsAt: null,
        expiredAt: null,
        windowType: null,
        actionVariant: null,
    }));
    return true;
}
export function* initializeWorker() {
    try {
        const assessment = (yield select(selectAssessment));
        const testInstanceId = (yield select(selectTestInstanceId));
        const step = yield select(selectStepDetail);
        const preInstructionState = (yield select(selectPreInstructionState));
        const isCandidateAuthEnabled = growthBook?.getFeatureValue(growthBookFeatureFlags.CANDIDATE_AUTH_ENABLED, false);
        const queryParams = (yield select(selectQueryParams));
        const shouldSkipProviewInit = queryParams?.force_submit === 'true';
        const isFaceAuthCompleted = preInstructionState.faceAuthCompleted;
        yield put(faceAuthStateUpdate({
            isCandidateAuthEnabled: isCandidateAuthEnabled,
            faceAuthCompleted: isFaceAuthCompleted,
        }));
        if (!testInstanceId && !assessment?.id) {
            throw new Error('Missing test_instance_id');
            return;
        }
        let assessmentDetail = (yield select(selectStartAssessmentResponse));
        if (step?.is_proctoring && !assessmentDetail) {
            yield call(startAssessmentWorker, {
                payload: { instance_id: testInstanceId, skipSession: true },
            });
            assessmentDetail = (yield select(selectStartAssessmentResponse));
            if (!assessmentDetail) {
                throw new Error('Unable to load assessment details');
            }
        }
        const isAssessmentCompleted = assessmentDetail?.instance_status === 'COMPLETED';
        if (step?.is_proctoring) {
            if (shouldSkipProviewInit) {
                yield put(startAssessment({ instance_id: testInstanceId }));
                return;
            }
            if (isAssessmentCompleted) {
                return;
            }
            const proviewSessionId = (yield select(selectProviewSessionUUID));
            const proviewInitStatus = (yield select(selectProviewInitStatus));
            if (proviewInitStatus === 'success' && proviewSessionId) {
                yield put(startAssessment({ instance_id: testInstanceId, session_uuid: proviewSessionId }));
            }
            else if (proviewInitStatus === 'failed') {
                throw new Error(`Proview session failed to initialize`);
            }
        }
        else if (!assessment?.id) {
            if (isAssessmentCompleted) {
                yield put(startAssessmentError({ error: ASSESSMENT_ALREADY_COMPLETED_UI_MESSAGE_KEY }));
                return;
            }
            // Custom logic for preform and face auth
            const { hasPreform, preformSubmitted } = preInstructionState || {};
            if (hasPreform) {
                if (preformSubmitted) {
                    if (isCandidateAuthEnabled) {
                        if (isFaceAuthCompleted) {
                            yield put(startAssessment({ instance_id: testInstanceId }));
                        }
                    }
                    else {
                        yield put(startAssessment({ instance_id: testInstanceId }));
                    }
                }
            }
            else if (isCandidateAuthEnabled) {
                if (isFaceAuthCompleted) {
                    yield put(startAssessment({ instance_id: testInstanceId }));
                }
            }
            else {
                yield put(startAssessment({ instance_id: testInstanceId }));
            }
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'Assessment initialize Worker',
            error: error,
        });
    }
}
// Resolves the self-book route before Proview, start or the registration form; true when the candidate must book first.
export function* shouldStopForSelfBooking(step) {
    // Only invites carrying wcs_id are self-booking; every other invite keeps the normal flow.
    const wcsId = (yield select(selectWcsId));
    if (!wcsId)
        return false;
    // The flag was read once, at fetch: drive_occurrences is only present when it was on. Step details that
    // never loaded (no step) or an excluded field resolve to CONTINUE, so a missing field never reads as "not booked".
    const occurrences = step?.drive_occurrences;
    const route = resolveSelfBookRoute({ wcsId, occurrences });
    if (occurrences) {
        const guests = getStepGuests(occurrences);
        cxSelfBookRouteResolved({ route, has_guest: guests.length > 0, is_booked: Boolean(findBookedGuest(guests)) });
    }
    // Always resolved for wcs_id invites, so useSelfBookRedirect never waits on a route that will not come.
    yield put(setSelfBookRoute(route));
    return route === SELF_BOOK_ROUTE.SLOT_BOOKING;
}
export function* getStepDetailsWorker(action) {
    try {
        const { step_id } = action.payload;
        // Fetch step details
        yield call(fetchStepDetailsWorker, { step_id });
        const step = yield select(selectStepDetail);
        // Form checks
        const formId = step?.step_forms?.find((f) => f.form?.type === 'REGISTRATION')?.form?.id ?? null;
        const formSubmitted = localStorageGetItem(ASSESSMENT_PRE_REGISTRATION_FORM);
        const isProctoring = step?.is_proctoring;
        const matchedWorkflowCandidateStep = getMatchedWorkflowCandidateStep(step);
        const status = matchedWorkflowCandidateStep?.status?.name ?? '';
        const normalizedStatus = typeof status === 'string' ? status.trim().toUpperCase() : '';
        const shouldRedirectToStatusError = STATUS_ERROR_STATUSES.includes(normalizedStatus);
        const latestTestInstanceId = getLatestTestInstanceId(matchedWorkflowCandidateStep);
        if (latestTestInstanceId) {
            const currentQueryParams = (yield select(selectQueryParams));
            if (String(latestTestInstanceId) !== (currentQueryParams?.test_instance_id ?? '')) {
                yield put(storeQueryParams({ ...currentQueryParams, test_instance_id: String(latestTestInstanceId) }));
            }
        }
        const restrictAssessmentEnabled = growthBook?.getFeatureValue(growthBookFeatureFlags.ENABLE_RESTRICT_ASSESSMENT, false);
        if (restrictAssessmentEnabled && shouldRedirectToStatusError) {
            yield put(setShouldRedirectToStatusError(true));
            return;
        }
        if (restrictAssessmentEnabled) {
            yield put(setShouldRedirectToStatusError(false));
        }
        if (yield* shouldStopForSelfBooking(step))
            return;
        const hasSubmittedForm = formId ? formSubmitted === 'true' : true;
        // Test only: forcibly mark registration form as submitted if it exists
        // const hasSubmittedForm = formId ? true : true;
        // Case 1: Form exists and NOT submitted → redirect to form
        if (formId && !hasSubmittedForm) {
            yield put(updatePreInstructionState({
                hasPreform: true,
                preformSubmitted: false,
                formId,
                faceAuthCompleted: false,
                isCandidateAuthEnabled: false,
            }));
            return;
        }
        if (step?.step_windows != null && !Array.isArray(step.step_windows)) {
            cxStepWindowApiError({
                error_type: 'unusable_window_data',
                step_id,
            });
        }
        const parsedStepWindows = Array.isArray(step?.step_windows) ? step.step_windows : [];
        const onboardingWindow = parsedStepWindows.find((window) => window?.window_type?.toUpperCase() === 'ONBOARDING');
        const rawActionWindow = parsedStepWindows.find((window) => window?.window_type?.toUpperCase() === 'ACTION');
        // Extend the ACTION window's end time by the candidate's accommodation percentage so every
        // gate/check below (expiry block, "within window" tracking, etc.) is consistent with the
        // extended boundary rather than locking an accommodated candidate out at the original one.
        const durationPercentageForWindow = getStepDurationPercentage(step);
        const actionWindow = extendWindowEnd(rawActionWindow, durationPercentageForWindow);
        const stepIdForSecureBrowser = String(step?.id || '');
        const proviewToken = growthBook?.getFeatureValue(growthBookFeatureFlags.CX_PROVIEW_TOKEN, '');
        const shouldUseV7Proview = step?.is_proctoring === true && Boolean(proviewToken);
        if (!shouldUseV7Proview && isSecureBrowserRequiredForStep(step?.flow_details)) {
            const testInstanceId = (yield select(selectTestInstanceId));
            const queryParams = (yield select(selectQueryParams));
            const customToken = (yield select(selectCustomToken));
            const shouldProceedPastSecureBrowserGate = ensureSecureBrowser({
                stepId: stepIdForSecureBrowser,
                testInstanceId,
                customToken,
                queryParamsCustomToken: queryParams?.custom_token,
                queryParamsTestInstanceId: queryParams?.test_instance_id,
                queryParamsStepId: queryParams?.step_id,
            });
            if (!shouldProceedPastSecureBrowserGate) {
                return;
            }
        }
        // Case 2: Form exists and submitted → check proctoring
        // Case 3: Form not present → check proctoring
        if (isProctoring) {
            const now = DateTime.fromMillis(Date.now());
            const actionWindowEnd = actionWindow?.ends_at ? DateTime.fromISO(actionWindow.ends_at) : null;
            const actionWindowStart = actionWindow?.starts_at ? DateTime.fromISO(actionWindow.starts_at) : null;
            const onboardingWindowStart = onboardingWindow?.starts_at ? DateTime.fromISO(onboardingWindow.starts_at) : null;
            const hasValidActionStart = Boolean(actionWindowStart?.isValid);
            const hasValidOnboardingStart = Boolean(onboardingWindowStart?.isValid);
            const shouldPrioritizeActionStart = Boolean(hasValidActionStart &&
                (!hasValidOnboardingStart || actionWindowStart.toMillis() <= onboardingWindowStart.toMillis()));
            const hasOnboardingWindow = Boolean(onboardingWindow?.starts_at);
            if (!onboardingWindow && !actionWindow) {
                cxStepWindowNoRestrictionFlow();
            }
            const actionWindowStartIso = actionWindowStart?.isValid ? actionWindowStart.toISO() : null;
            const onboardingWindowStartRaw = onboardingWindow?.starts_at ?? null;
            const shouldTrackOnboardingFallbackToAction = Boolean(actionWindow &&
                (!onboardingWindow?.starts_at ||
                    !hasValidOnboardingStart ||
                    (hasValidActionStart && actionWindowStart.toMillis() <= onboardingWindowStart.toMillis())));
            if (shouldTrackOnboardingFallbackToAction) {
                cxStepWindowOnboardingFallbackToAction({
                    onboarding_starts_at_raw: onboardingWindowStartRaw,
                    action_starts_at_raw: actionWindow?.starts_at ?? null,
                });
            }
            const isActionStarted = Boolean(actionWindowStart?.isValid && now >= actionWindowStart);
            const isWithinActionWindow = Boolean(actionWindowStart?.isValid && now >= actionWindowStart && (!actionWindowEnd?.isValid || now < actionWindowEnd));
            const actionWaitVariant = shouldPrioritizeActionStart || !hasOnboardingWindow ? 'noOnboarding' : 'default';
            const onboardingWindowEnd = onboardingWindow?.ends_at ? DateTime.fromISO(onboardingWindow.ends_at) : null;
            const isWithinOnboardingWindow = Boolean(onboardingWindowStart?.isValid &&
                onboardingWindowEnd?.isValid &&
                now >= onboardingWindowStart &&
                now < onboardingWindowEnd);
            const isLateOnboardingButActionAllowed = Boolean(onboardingWindowEnd?.isValid && now >= onboardingWindowEnd && isWithinActionWindow);
            const earliestWindow = hasValidActionStart && hasValidOnboardingStart
                ? actionWindowStart.toMillis() <= onboardingWindowStart.toMillis()
                    ? { type: 'ACTION', start: actionWindowStart }
                    : { type: 'ONBOARDING', start: onboardingWindowStart }
                : hasValidActionStart
                    ? { type: 'ACTION', start: actionWindowStart }
                    : hasValidOnboardingStart
                        ? { type: 'ONBOARDING', start: onboardingWindowStart }
                        : null;
            if (earliestWindow && now < earliestWindow.start) {
                const minutesBeforeWindow = Math.max(0, Math.ceil(earliestWindow.start.diff(now, 'minutes').minutes));
                cxStepWindowEarlyJoinBlocked({
                    window_type: earliestWindow.type,
                    minutes_before_window: minutesBeforeWindow,
                });
            }
            if (!isActionStarted && !shouldPrioritizeActionStart) {
                yield* waitForProctoringWindow(onboardingWindow, 'ONBOARDING');
            }
            // Show action countdown when: no onboarding window, action starts first,
            // or onboarding window has already expired (candidate arrives after onboarding ended but before action starts).
            const isOnboardingExpired = Boolean(onboardingWindowEnd?.isValid && now >= onboardingWindowEnd);
            if (!hasOnboardingWindow || shouldPrioritizeActionStart || isOnboardingExpired) {
                yield* waitForProctoringWindow(actionWindow, 'ACTION', actionWaitVariant);
            }
            let assessmentDetail = (yield select(selectStartAssessmentResponse));
            const isRejoin = Boolean(assessmentDetail);
            if (!assessmentDetail) {
                const testInstanceId = (yield select(selectTestInstanceId));
                yield call(startAssessmentWorker, {
                    payload: { instance_id: testInstanceId, skipSession: true },
                });
                assessmentDetail = (yield select(selectStartAssessmentResponse));
            }
            if (assessmentDetail?.instance_status === 'COMPLETED') {
                return;
            }
            if (isWithinOnboardingWindow && !shouldPrioritizeActionStart) {
                cxStepWindowOnboardingAllowed();
            }
            if (isWithinActionWindow) {
                if (isLateOnboardingButActionAllowed) {
                    const minutesPastOnboardingEnd = Math.max(0, Math.ceil(now.diff(onboardingWindowEnd, 'minutes').minutes));
                    cxStepWindowLateOnboardingAllowed({ minutes_past_onboarding_end: minutesPastOnboardingEnd });
                }
                const actionAllowedTrackingKey = [
                    step?.id ?? step_id ?? 'unknown-step',
                    matchedWorkflowCandidateStep?.id ?? 'unknown-workflow-step',
                    actionWindowStartIso ?? 'unknown-action-window-start',
                ].join(':');
                if (!actionAllowedTrackedSessionKeys.has(actionAllowedTrackingKey)) {
                    cxStepWindowActionAllowed({ is_rejoin: isRejoin });
                    actionAllowedTrackedSessionKeys.add(actionAllowedTrackingKey);
                }
            }
            // Proctored: block if action window has expired — do not start Proview or face auth
            if (actionWindowEnd?.isValid && now >= actionWindowEnd) {
                yield put(setOnboardingWaitInfo({
                    shouldShow: true,
                    startsAt: null,
                    expiredAt: actionWindow.ends_at,
                    windowType: 'ACTION',
                    actionVariant: actionWaitVariant,
                }));
                return;
            }
            yield put(setShouldStartProview({ source: 'assessment' }));
        }
        else {
            const now = DateTime.fromMillis(Date.now());
            const actionWindowEnd = actionWindow?.ends_at ? DateTime.fromISO(actionWindow.ends_at) : null;
            // Non-proctored (without Proview): enforce only action end-time expiry.
            if (actionWindow?.ends_at && actionWindowEnd?.isValid && now >= actionWindowEnd) {
                yield put(setOnboardingWaitInfo({
                    shouldShow: true,
                    startsAt: null,
                    expiredAt: actionWindow.ends_at,
                    windowType: 'ACTION',
                    actionVariant: 'noOnboarding',
                }));
                return;
            }
            yield call(initializeWorker);
        }
        // if (callback) {
        //   yield call(callback.onSuccess, step as StepDetails);
        // }
    }
    catch (error) {
        // The gate may not have run; never leave a wcs_id invite waiting on a route (no-op for other invites).
        yield put(setSelfBookRoute(SELF_BOOK_ROUTE.CONTINUE));
        yield call(catchError, {
            title: 'Assessment initialize Worker',
            error: error,
        });
    }
}
// Network-only so a refetch after booking sees the new guest. A failure (e.g. CX-6077 not deployed yet) must
// not block step details: it returns undefined, which routes the candidate to the normal flow.
export function* fetchStepDriveOccurrencesWorker(step_id) {
    try {
        const response = (yield call(fetchData, {
            queryString: GET_STEP_DRIVE_OCCURRENCES,
            queryVariables: { step_id },
            queryKey: 'canx_get_step_details',
            forceRefresh: true,
        }));
        return response?.drive_occurrences;
    }
    catch (error) {
        yield call(catchError, {
            title: 'Fetching step drive occurrences failed.',
            error: error,
        });
        return undefined;
    }
}
export function* fetchStepDetailsWorker({ step_id }) {
    try {
        if (step_id) {
            const wcsId = (yield select(selectWcsId));
            const stepDetailsCall = call(fetchData, {
                queryString: GET_STEP_DETAILS,
                queryVariables: { step_id },
                queryKey: 'canx_get_step_details',
            });
            // Self-book invites also fetch the drive data, in parallel; every other invite sends only GET_STEP_DETAILS.
            const [response, driveOccurrences] = shouldIncludeDriveOccurrences(wcsId)
                ? (yield all([stepDetailsCall, call(fetchStepDriveOccurrencesWorker, step_id)]))
                : [(yield stepDetailsCall), undefined];
            if (response) {
                if (response?.step_windows != null && !Array.isArray(response.step_windows)) {
                    cxStepWindowApiError({
                        error_type: 'unusable_window_data',
                        step_id,
                    });
                }
                yield put(storeStepDetails(driveOccurrences ? { ...response, drive_occurrences: driveOccurrences } : response));
            }
            else {
                cxStepWindowApiError({
                    error_type: 'empty_response',
                    step_id,
                });
            }
        }
    }
    catch (error) {
        const errorMessage = error instanceof Error ? error.message.toLowerCase() : '';
        const errorType = errorMessage.includes('timeout') ? 'timeout' : 'request_failed';
        cxStepWindowApiError({
            error_type: errorType,
            step_id,
        });
        yield call(catchError, {
            title: 'Fetching step details failed.',
            error: error,
        });
    }
}
export function* proviewSessionInitializedWatcher() {
    while (true) {
        yield take(proviewSessionInitialized.type);
        // Immediately start assessment worker based on proview status
        yield put(setProviewSessionInitStatus({ status: 'success' }));
        yield call(initializeWorker);
    }
}
export function* startAssessmentWorker(action) {
    try {
        const testInstanceIdFromAction = action?.payload?.instance_id;
        const testInstanceId = typeof testInstanceIdFromAction === 'number'
            ? testInstanceIdFromAction
            : (yield select(selectTestInstanceId));
        if (!testInstanceId) {
            throw new Error('Missing test_instance_id');
        }
        const shouldIncludeSession = !action?.payload?.skipSession;
        const startAssessmentPayload = {
            instance_id: testInstanceId,
        };
        if (shouldIncludeSession) {
            const step = (yield select(selectStepDetail));
            const isProctoredAssessment = Boolean(step?.is_proctoring);
            if (isProctoredAssessment) {
                const sessionUuidFromAction = action?.payload?.session_uuid;
                const sessionUuidFromStore = (yield select(selectProviewSessionUUID));
                const sessionUuidFromLocalStorage = (yield call(localStorageGetItem, PROVIEW_SESSION_UUID));
                const proviewSessionId = sessionUuidFromAction || sessionUuidFromStore || sessionUuidFromLocalStorage;
                if (proviewSessionId === V8) {
                    // V8 flow intentionally starts without session_uuid.
                }
                else if (proviewSessionId) {
                    startAssessmentPayload.session_uuid = proviewSessionId;
                }
                else {
                    throw new Error('Missing session_uuid for proctored assessment');
                }
            }
        }
        const response = (yield call(postData, {
            queryString: START_ASSESSMENT,
            payload: startAssessmentPayload,
        }));
        const result = response?.data?.canx_start_assessment[0];
        if (result?.success) {
            const processedData = (yield call(processStartAssessmentResponse, result.data));
            yield put(storeAssessment(processedData));
        }
        else {
            const err = Array.isArray(result?.error_message) ? result?.error_message?.[0] : result?.error_message;
            const errorMessage = hasUnauthorizedStartAssessmentToken([err])
                ? UNAUTHORIZED_START_ASSESSMENT_UI_MESSAGE_KEY
                : hasInviteExpiredToken([err])
                    ? INVITE_EXPIRED_UI_MESSAGE_KEY
                    : `Failed to start assessment: ${err}`;
            throw Error(errorMessage);
        }
    }
    catch (error) {
        const err = error;
        const graphQlErrors = error?.graphQLErrors;
        const graphQlErrorMessages = Array.isArray(graphQlErrors)
            ? graphQlErrors.map((graphQlError) => graphQlError?.message).filter((message) => !!message)
            : [];
        const isUnauthorizedStartAssessmentError = hasUnauthorizedStartAssessmentToken([
            err?.message,
            ...graphQlErrorMessages,
        ]);
        const isInviteExpiredError = hasInviteExpiredToken([err?.message, ...graphQlErrorMessages]);
        const normalizedError = isUnauthorizedStartAssessmentError
            ? Error(UNAUTHORIZED_START_ASSESSMENT_UI_MESSAGE_KEY)
            : isInviteExpiredError
                ? Error(INVITE_EXPIRED_UI_MESSAGE_KEY)
                : err;
        yield call(catchError, {
            title: 'startAssessmentWorker',
            error: normalizedError,
            skipToast: isUnauthorizedStartAssessmentError || isInviteExpiredError,
        });
        yield put(startAssessmentError({ error: normalizedError?.message }));
    }
}
export function* startSectionWorker({ payload }) {
    try {
        const currentAssessmentSection = (yield select(selectAssessmentSection));
        const currentSectionTypeInfo = assessmentType(currentAssessmentSection);
        const testInstanceId = (yield select(selectTestInstanceId));
        const response = (yield call(postData, {
            queryString: START_SECTION,
            payload: { instance_id: testInstanceId },
        }));
        const result = response?.data?.canx_start_next_section[0];
        if (result?.success) {
            const processedData = (yield call(getConstrainedProcessedSectionData, result.data));
            const sectionTypeInfo = assessmentType(processedData.assessmentSection);
            // TODO: Remove once saga refactor to support skip for async assessments is completed.
            // Longform, like async, never supports skipping regardless of allow_skipping.
            const allowSkipSection = sectionTypeInfo.isAssessmentTypeAsync || sectionTypeInfo.isAssessmentTypeLongForm
                ? false
                : processedData.assessmentSection?.allow_skipping;
            // todo(atul): !processedData.answers.length && !processedData.assessmentSection this condition implement here till longform not generating the score
            // handle-case: if user submit the longform and after feedback if try to join again then it redirect to thank you page rather than result page
            if (!processedData.answers.length && !processedData.assessmentSection) {
                yield call(payload.callback.onSuccess, {
                    redirectTo: '/assessment/thank-you',
                });
            }
            else if (!processedData.assessmentSection && currentSectionTypeInfo.isAssessmentTypeCoding) {
                // Intentionally uses the pre-call section type: coding sections skip score view and always redirect to feedback.
                // Feedback itself renders PostInstructions inline first when the step has outro content.
                yield call(payload.callback.onSuccess, {
                    redirectTo: '/assessment/feedback',
                });
            }
            else if (!processedData.assessmentSection) {
                yield call(payload.callback.onSuccess, {
                    redirectTo: '/assessment/result',
                });
            }
            else if (!processedData.next_question_id &&
                !allowSkipSection &&
                some(processedData.answers, { id: 'has_to_save' }) &&
                !sectionTypeInfo.isAssessmentTypeExternal) {
                yield put(storeSection(processedData));
                yield call(payload.callback.onSuccess, {
                    redirectTo: '/assessment/submit',
                });
            }
            else {
                yield put(storeSection(processedData));
                if (processedData.assessmentSection?.id && processedData.parsedMaxSectionTime > 0) {
                    yield put(anchorSectionTimer({
                        sectionId: processedData.assessmentSection.id,
                        budgetSeconds: processedData.parsedMaxSectionTime,
                        source: 'section',
                    }));
                }
                const nextSectionTypeInfo = assessmentType(result.data?.assessment_section);
                if (result.data?.id) {
                    yield call(getAppStateWorker, {
                        payload: {
                            section_id: result.data?.id,
                            trackAsyncResume: nextSectionTypeInfo.isAssessmentTypeAsync,
                        },
                        type: getAppState.type,
                    });
                }
                const appState = (yield select(selectAppState));
                const constrainedProcessedData = (yield call(getConstrainedProcessedSectionData, result.data, appState));
                yield put(storeSection(constrainedProcessedData));
                if (constrainedProcessedData.assessmentSection?.id && constrainedProcessedData.parsedMaxSectionTime > 0) {
                    yield put(anchorSectionTimer({
                        sectionId: constrainedProcessedData.assessmentSection.id,
                        budgetSeconds: constrainedProcessedData.parsedMaxSectionTime,
                        source: 'resume',
                    }));
                }
                if (payload.callback.onSuccess) {
                    yield call(payload.callback.onSuccess);
                }
                yield call(fetchQuestionsListWorker);
            }
        }
        else {
            const err = Array.isArray(result?.error_message) ? result?.error_message?.[0] : result?.error_message;
            throw Error(`Failed to start section: ${err}`);
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'startSectionWorker',
            error: error,
            skipToast: true, // component owns error presentation via onError callback
        });
        if (payload?.callback?.onError) {
            yield call(payload.callback.onError, error);
        }
    }
}
export function* getNextSectionWorker({ payload, }) {
    try {
        const testInstanceId = (yield select(selectTestInstanceId));
        const response = (yield call(postData, {
            queryString: START_SECTION,
            payload: { instance_id: testInstanceId },
        }));
        const result = response?.data?.canx_start_next_section[0];
        if (result?.success) {
            const stepForNextSection = (yield select(selectStepDetail));
            const processedData = (yield call(processStartSectionResponse, result.data, undefined, stepForNextSection));
            const nextSectionDurationPercentage = getStepDurationPercentage(stepForNextSection);
            const constrainedProcessedData = {
                ...processedData,
                parsedMaxSectionTime: getWindowConstrainedTime(processedData.parsedMaxSectionTime, findActionWindow(stepForNextSection), nextSectionDurationPercentage),
            };
            if (processedData.assessmentSection) {
                yield put(resetSectionState());
                yield put(storeSection(constrainedProcessedData));
                if (constrainedProcessedData.assessmentSection?.id && constrainedProcessedData.parsedMaxSectionTime > 0) {
                    yield put(anchorSectionTimer({
                        sectionId: constrainedProcessedData.assessmentSection.id,
                        budgetSeconds: constrainedProcessedData.parsedMaxSectionTime,
                        source: 'section',
                    }));
                }
                yield call(payload.callback.onSuccess, {
                    redirectTo: '/assessment/invite',
                });
                yield call(fetchQuestionsListWorker);
            }
            else if (payload?.async || payload?.hasLongForm || payload?.isExternal || payload?.isCoding) {
                // Feedback itself renders PostInstructions inline first when the step has outro content.
                yield call(payload.callback.onSuccess, { redirectTo: '/assessment/feedback' });
            }
            else {
                yield call(payload.callback.onSuccess, { redirectTo: '/assessment/result' });
            }
        }
        else {
            const err = Array.isArray(result?.error_message) ? result?.error_message?.[0] : result?.error_message;
            throw Error(`Failed to start section: ${err}`);
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'getNextSectionWorker',
            error: error,
        });
        if (payload?.callback?.onError) {
            yield call(payload.callback.onError, error);
        }
    }
}
export function* fetchNextQuestionByIdWorker({ payload }) {
    try {
        const nextQuestionId = (yield select(selectNextQuestionId));
        const startSectionResponse = (yield select(selectStartSectionResponse));
        // const questions = (yield select(selectAllQuestion)) as QuestionOutput[] | null;
        const sectionTypeInfo = assessmentType(startSectionResponse.assessment_section);
        // TODO: Remove once saga refactor to support skip for async assessments is completed.
        // Longform, like async, never supports skipping regardless of allow_skipping.
        const allowSkipSection = sectionTypeInfo.isAssessmentTypeAsync || sectionTypeInfo.isAssessmentTypeLongForm
            ? false
            : (startSectionResponse.assessment_section?.allow_skipping ?? false);
        if (!nextQuestionId && !allowSkipSection) {
            yield put({ type: stopHeartBeat });
            if (payload.callback.onSuccess) {
                yield call(payload.callback.onSuccess, { redirectTo: '/assessment/submit' });
            }
        }
        else {
            let result;
            const questions = (yield select(selectAllQuestion));
            if (questions?.length) {
                let question;
                if (allowSkipSection && !nextQuestionId && questions?.length) {
                    question = questions[questions.length - 1];
                }
                else {
                    question = nextQuestionId ? find(questions, { id: nextQuestionId }) : null;
                }
                if (question) {
                    result = {
                        success: true,
                        data: [question],
                    };
                }
                else {
                    result = {
                        error_message: 'Question not found in question list',
                    };
                }
            }
            else {
                const startSectionResponse = (yield select(selectStartSectionResponse));
                const response = (yield call(postData, {
                    queryString: GET_NEXT_QUESTION_BY_ID,
                    payload: {
                        question_id: nextQuestionId,
                        section_id: startSectionResponse?.id,
                    },
                    spreadPayload: true,
                }));
                result = response?.data?.ae_get_next_question;
            }
            if (result?.success) {
                const questionIds = (yield select(selectQuestionIds));
                const question = result?.data?.[0];
                const nextQuestionId = getNextQuestionId(questionIds, question?.id);
                yield put(storeNextQuestion({ data: result.data?.[0], next_question_id: nextQuestionId }));
                if (payload.callback.onSuccess) {
                    yield call(payload.callback.onSuccess);
                }
            }
            else {
                const err = Array.isArray(result?.error_message)
                    ? result?.error_message?.[0]
                    : result?.error_message;
                throw Error(`Failed to start section: ${err}`);
            }
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'fetchNextQuestionByIdWorker',
            error: error,
        });
        if (payload?.callback?.onError) {
            yield call(payload.callback.onError, error);
        }
    }
}
export function* fetchQuestionsListWorker() {
    try {
        const testInstanceId = (yield select(selectTestInstanceId));
        const startSectionResponse = (yield select(selectStartSectionResponse));
        const assessmentSection = (yield select(selectAssessmentSection));
        const step = (yield select(selectStepDetail));
        const sectionType = assessmentType(assessmentSection);
        const isExternalSection = sectionType.isAssessmentTypeExternal;
        const isIvySection = sectionType.isAssessmentTypeIvy;
        let response = null;
        if (isExternalSection || isIvySection) {
            response = { data: { ae_get_question_list: { data: [] } } };
        }
        else {
            response = (yield call(postData, {
                queryString: GET_ALL_QUESTION,
                payload: {
                    instance_id: testInstanceId,
                    section_id: startSectionResponse?.id,
                },
                spreadPayload: true,
            }));
        }
        if (response) {
            const questions = response?.data?.ae_get_question_list?.data || [];
            if (questions.length === 0 && !isExternalSection)
                throw catchException.apiError('No questions found for the section', {
                    sectionId: startSectionResponse?.id,
                });
            const totalAssessmentTime = getCentralizeSectionTime(sectionType, questions, assessmentSection, step);
            const currentParsedTime = yield select(selectParsedMaxSectionTime);
            // Always overwrite so a section without a timer does not show the previous section's duration.
            yield put(updateTotalAssessmentTime(totalAssessmentTime));
            const durationPercentage = getStepDurationPercentage(step);
            if (durationPercentage) {
                const baseAssessmentTime = getCentralizeSectionTime(sectionType, questions, assessmentSection);
                candidateViewExtendedDuration({
                    step_name: step?.name ?? '',
                    base_duration: baseAssessmentTime,
                    extended_duration: totalAssessmentTime,
                    delivery_type: assessmentSection?.section_type ?? '',
                });
            }
            if (currentParsedTime === null && !sectionType.isAssessmentTypeAsync) {
                yield put(setUpdatedParsedMaxSectionTime(totalAssessmentTime));
            }
            yield put(storeQuestionList({ data: questions }));
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'fetchQuestionsListWorker',
            error: error,
        });
    }
}
export function* saveAnswerWorker({ payload, }) {
    try {
        const currentQuestion = payload?.data?.question || (yield select(selectCurrentQuestion));
        const startSectionResponse = (yield select(selectStartSectionResponse));
        const questions = (yield select(selectAllQuestion));
        const assessmentSection = (yield select(selectAssessmentSection));
        const allowSkipSection = startSectionResponse.assessment_section?.allow_skipping ?? false;
        const showAnswerFeedback = assessmentSection?.is_show_answer ?? false;
        let result;
        const sectionTypeInfo = assessmentType(startSectionResponse.assessment_section);
        if (questions && !sectionTypeInfo.isAssessmentTypeCoding) {
            result = parseSubmittedAnswer(payload, currentQuestion, startSectionResponse);
        }
        else {
            const response = (yield call(postData, {
                queryString: SAVE_ANSWERS,
                payload: {
                    question_id: payload?.data?.question_id || currentQuestion.id,
                    section_id: startSectionResponse.id,
                    answered_at: getCurrentDate()?.toUTC().toISO(),
                    choice: payload.data.choice,
                    code_response: payload.data.code_response,
                },
            }));
            result = response?.data?.ae_save_answer[0];
        }
        if (result?.success) {
            // Coding questions' API response has no submission marker; stamp one to match parseSubmittedAnswer's contract.
            const answerData = { ...result.data, is_answer_submitted: true };
            yield put(storeAnswer(answerData));
            // Gate the "pause for feedback" decision on the section flag too, not just correctness being resolvable.
            const isShowCorrectAnswer = showAnswerFeedback && typeof answerData.is_correct_choice === 'boolean';
            if (isShowCorrectAnswer) {
                // Flush immediately (bypassing the debounced watcher) so a refresh restores the answered state.
                yield call(saveAppStateWorker, saveAppState({ data: { remaining_time: payload.remainingTime } }));
            }
            else {
                yield put(saveAppState({ data: { remaining_time: payload.remainingTime } }));
            }
            if (!isShowCorrectAnswer && !allowSkipSection) {
                yield put(fetchNextQuestionById({ callback: payload.callback }));
            }
            else if (payload.callback.onSuccess) {
                yield call(payload.callback.onSuccess);
            }
        }
        else {
            const err = Array.isArray(result?.error_message) ? result?.error_message?.[0] : result?.error_message;
            throw Error(`Failed to save answer:  ${err}`);
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'saveAnswerWorker',
            error: error,
        });
        if (payload?.callback?.onError) {
            yield call(payload.callback.onError, error);
        }
    }
}
export function* saveBulkAnswerWorker({ payload }) {
    try {
        const allAnswers = (yield select(selectAnswers));
        const answers = (yield call(filter, allAnswers, (obj) => obj.id === 'has_to_save'));
        const eventOption = {
            Type: 'Manual',
        };
        bulkAnswerSubmitted(eventOption);
        if (answers.length) {
            const formattedAnswerObject = answers.map((item) => {
                const formattedAnswer = {
                    question_id: item.question_id,
                    section_id: item.section_id,
                    answered_at: item.answered_at,
                    choice: item.choice,
                };
                if (item?.text_response?.doc) {
                    formattedAnswer.text_response = item.text_response;
                }
                if (item?.code_response) {
                    formattedAnswer.code_response = item.code_response;
                }
                return formattedAnswer;
            });
            const response = (yield call(postData, {
                queryString: SAVE_ANSWERS,
                payload: formattedAnswerObject,
            }));
            const parseBulkSavedAnswerResponse = (yield call(parseBulkSavedAnswer, response, allAnswers));
            if (payload.callback.onSuccess && parseBulkSavedAnswerResponse) {
                yield put(storeBulkAnswer(parseBulkSavedAnswerResponse.formattedAnswers));
                yield call(payload.callback.onSuccess, parseBulkSavedAnswerResponse);
            }
        }
        else {
            yield call(payload.callback.onSuccess);
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'saveBulkAnswerWorker',
            error: error,
            skipToast: true, // component owns error presentation for this entire flow
        });
        if (payload?.callback?.onError) {
            yield call(payload.callback.onError, error);
        }
    }
}
export function* stopSectionWorker({ payload, type, }) {
    try {
        const startSectionResponse = (yield select(selectStartSectionResponse));
        const response = (yield call(postData, {
            queryString: STOP_SECTION,
            payload: {
                section_id: startSectionResponse.id,
            },
            spreadPayload: true,
        }));
        const result = response?.data?.ae_stop_section[0];
        if (result?.success) {
            const lastQuestion = (yield select(selectCurrentQuestion));
            yield put(storeStopSection({ startSectionResponse: result.data }));
            // Clear assessmentSection immediately so a timer-triggered remount of AssessmentSubmission
            // sees no active section and does not fire a second stop_section call.
            yield put(resetSectionState());
            if (lastQuestion) {
                // Submission's isCodingQuestionType guard reads currentQuestion, which resetSectionState
                // just cleared; restore it (mirrors assessmentSection, restored below via startAssessmentWorker)
                // so Submission can still detect a coding question and skip the score flow.
                yield put(storeNextQuestion({ data: lastQuestion, next_question_id: null }));
            }
            // Refresh the assessment record before routing so thank-you can see the completed state.
            yield call(startAssessmentWorker, {
                payload: { skipSession: true },
            });
            yield call(getNextSectionWorker, { payload, type });
        }
        else {
            const err = Array.isArray(result?.error_message) ? result?.error_message?.[0] : result?.error_message;
            throw Error(`Failed to stop section:  ${err}`);
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'stopSectionWorker',
            error: error,
            skipToast: true,
        });
    }
    finally {
        yield put(stopSectionSettled());
    }
}
export function* fetchTcfMeasureWorker({ payload: { callback }, }) {
    const testInstanceId = (yield select(selectTestInstanceId));
    // Best rows seen so far, so an exhausted budget can still render the numeric score.
    let latestOverallData = [];
    for (let attempt = 1; attempt <= TCF_MEASURE_MAX_ATTEMPTS; attempt += 1) {
        try {
            const response = (yield call(fetchData, {
                queryString: GET_TCF_MEASUREMENT,
                queryKey: 'ae_get_instance_score',
                queryVariables: { test_instance_id: testInstanceId },
                forceRefresh: true,
                context: {},
            }));
            const overallData = getOverallSections(response?.[0]?.data ?? []);
            if (overallData.length) {
                latestOverallData = overallData;
            }
            // The score is ready only when BOTH hold:
            //   1. an overall row exists at all — the original poll condition;
            //   2. that row carries the ordinal result label. The section-level row lands before the
            //      label does, and settling on it shows the candidate the wrong verbiage.
            // Anything else falls through to the retry delay below.
            if (overallData.length > 0 && hasResultLabel(overallData)) {
                yield put(storeTcfMeasurement(overallData));
                if (callback?.onSuccess) {
                    yield call(callback.onSuccess);
                }
                return;
            }
        }
        catch (error) {
            yield call(catchError, {
                title: 'fetchTcfMeasureWorker',
                error: error,
                skipToast: true,
            });
        }
        if (attempt < TCF_MEASURE_MAX_ATTEMPTS) {
            yield delay(TCF_MEASURE_RETRY_DELAY_MS);
        }
    }
    // Budget exhausted. Store what we have rather than stranding the candidate on a spinner —
    // storeTcfMeasurement is what clears request.tcfLoading — so the numeric score still renders.
    // The score page shows no verbiage, since no result label ever arrived.
    yield call(catchError, {
        title: 'fetchTcfMeasureWorker',
        error: new Error(`Result label unavailable after ${TCF_MEASURE_MAX_ATTEMPTS} attempts`),
        skipToast: true,
    });
    yield put(storeTcfMeasurement(latestOverallData));
    if (callback?.onError) {
        yield call(callback.onError);
    }
    else if (callback?.onSuccess) {
        yield call(callback.onSuccess);
    }
}
export function* saveAppStateWorker({ payload, }) {
    const isCodeSoloSave = Boolean(payload?.data?.code_response);
    try {
        const startSectionResponse = (yield select(selectStartSectionResponse));
        if (!startSectionResponse?.id) {
            // Section not ready yet (navigation race / session restore timing) — skip this save.
            return;
        }
        const { id } = startSectionResponse;
        const answers = (yield select(selectAnswers));
        const updatedAnswers = [...(answers || [])];
        if (payload?.data?.code_response && payload?.data?.question_id) {
            const codeLanguage = get(payload, 'data.code_response.doc.language_used.language');
            const answerIndex = findIndex(updatedAnswers, (answer) => {
                if (answer?.question_id !== payload.data.question_id)
                    return false;
                if (!codeLanguage)
                    return true;
                const answerLang = get(answer, 'code_response.doc.language_used.language');
                return answerLang === codeLanguage;
            });
            const nextAnswer = {
                ...(answerIndex >= 0 ? updatedAnswers[answerIndex] : {}),
                section_id: id,
                question_id: payload.data.question_id,
                code_response: payload.data.code_response,
            };
            if (answerIndex >= 0) {
                updatedAnswers[answerIndex] = nextAnswer;
            }
            else {
                updatedAnswers.push(nextAnswer);
            }
        }
        const currentQuestion = (yield select(selectCurrentQuestion));
        const currentSectionTimer = (yield select(selectCurrentSectionTimer));
        const nextQuestionId = yield select(selectNextQuestionId);
        const formattedPayload = {
            section_id: id,
            state: [
                {
                    remaining_time: payload?.data?.remaining_time,
                    elapsedSeconds: currentSectionTimer?.elapsedSeconds,
                    answers: updatedAnswers,
                    currentQuestionId: currentQuestion?.id,
                    nextQuestionId,
                },
            ],
        };
        yield call(localStorageSetItem, APP_STATE_PAYLOAD, JSON.stringify({
            [formattedPayload.section_id]: formattedPayload,
        }));
        const response = (yield call(postData, {
            queryString: CANX_UPSERT_APP_STATE,
            payload: formattedPayload,
        }));
        const result = response?.data?.canx_upsert_app_state[0];
        if (result?.success) {
            yield put(storeAppState(result?.data));
            if (isCodeSoloSave && payload?.callback?.onSuccess) {
                yield call(payload.callback.onSuccess);
            }
        }
        else {
            yield call(catchError, {
                title: 'saveAppStateWorker',
                error: Error('Failed to save the appState to server'),
                extraScope: { key: 'appState', value: JSON.stringify(formattedPayload) },
                skipToast: true,
            });
            if (isCodeSoloSave && payload?.callback?.onError) {
                yield call(payload.callback.onError, Error('Failed to save the appState to server'));
            }
        }
    }
    catch (error) {
        captureException(error, { tags: { saga: 'saveAppStateWorker' } });
        if (isCodeSoloSave && payload?.callback?.onError) {
            yield call(payload.callback.onError, error);
        }
        // yield call(catchError, {
        //   title: 'saveAppStateWorker',
        //   error: error as Error,
        //   skipToast: true,
        // });
    }
}
export function* syncAppStateWorker({ payload, }) {
    try {
        const appState = payload?.app_state;
        if (appState?.state?.length) {
            yield call(syncAppStateMessage, appState);
            yield put(setCodeSoloStatus('CODE_SOLO_INIT_COMPLETED'));
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'syncAppStateWorker',
            error: error,
            skipToast: true,
        });
    }
}
export function* createTestInstanceWorker({ payload, }) {
    try {
        const testInstanceId = (yield select(selectTestInstanceId));
        if (testInstanceId) {
            const response = (yield call(postData, {
                queryString: CREATE_TEST_INSTANCE,
                payload: {
                    test_instance_id: testInstanceId,
                    reattempt: true,
                },
                spreadPayload: true,
            }));
            const result = response?.data?.canx_create_test_instance[0];
            if (result?.success) {
                const queryParams = (yield select(selectQueryParams));
                const test_instance_id = result?.data?.test_instance_id;
                if (test_instance_id) {
                    const currentStep = (yield select(selectStepDetail));
                    yield put(storeQueryParams({ ...queryParams, test_instance_id: String(test_instance_id) }));
                    yield put(setTestInstanceId(test_instance_id));
                    if (currentStep?.is_proctoring) {
                        // Proctored: initialize() wipes Redux state so ProView can re-arm cleanly, but
                        // proviewSlice lives separately and needs its own reset — use the dedicated
                        // resetProviewSession action rather than proviewSessionInitialized({sessionUUID: ''}),
                        // whose action type also triggers proviewSessionInitializedWatcher.
                        yield put(initialize({}));
                        yield put(resetProviewSession());
                        yield call(removeLocalStorageItem, PROVIEW_SESSION_UUID);
                        // Tear down the completed attempt's SDK globals/DOM synchronously here rather than
                        // relying on ProviewContext's testInstanceId-change effect, which may commit after
                        // ProviewStartGate already re-armed with the stale, already-completed SDK instance.
                        yield call(resetProviewSDKState);
                        if (currentStep) {
                            // Restore step so isLoadingStepDetails guard on Invite doesn't block indefinitely.
                            yield put(storeStepDetails(currentStep));
                        }
                        // Re-store testInstanceId: initialize() above reset the assessment slice, clearing it.
                        yield put(setTestInstanceId(test_instance_id));
                        // Populate the new instance data before navigation, else selectStartAssessmentResponse
                        // still holds the previous COMPLETED instance and getStepDetailsWorker blocks ProView.
                        yield call(startAssessmentWorker, {
                            payload: { instance_id: test_instance_id, skipSession: true },
                        });
                        // Re-arm ProView explicitly: getStepDetailsWorker won't dispatch this again since
                        // step_id is unchanged, and the previous session's completion left shouldStartProview
                        // false — without this the Invite page stays stuck in a permanent loading state.
                        yield put(setShouldStartProview({ source: 'reattempt' }));
                        yield call(initializeWorker);
                    }
                    else {
                        // Non-proctored: skip initialize() — resetting Redux state would clear tcfMeasurement,
                        // causing a brief zero-score ScorePage flash. Call startAssessmentWorker directly to
                        // bypass the two chained debounces (initializeWatcher → startAssessmentWatcher).
                        yield call(startAssessmentWorker, {
                            payload: { instance_id: test_instance_id },
                        });
                    }
                    if (payload.callback.onSuccess) {
                        yield call(payload.callback.onSuccess);
                    }
                }
                else {
                    throw Error('createTestInstanceWorker: Test instance id not found in the reattempt response');
                }
            }
            else {
                const err = Array.isArray(result?.error_message)
                    ? result?.error_message?.[0]
                    : result?.error_message;
                throw Error(`Failed to createTestInstance: ${err}`);
            }
        }
        else {
            throw Error('createTestInstance: No test instance id found');
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'createTestInstance',
            error: error,
        });
        if (payload?.callback?.onError)
            yield call(payload.callback.onError, error);
    }
}
export function* getAppStateWorker({ payload, }) {
    try {
        if (payload?.section_id) {
            const localState = JSON.parse(localStorageGetItem(APP_STATE_PAYLOAD) || '{}');
            const response = (yield call(fetchData, {
                queryString: CANX_GET_APP_STATE,
                queryVariables: {
                    section_id: Number(payload?.section_id),
                },
            }));
            const result = get(response, 'canx_get_app_state.0', null);
            let appState = result;
            const sectionLocalState = localState && localState[payload.section_id];
            if (sectionLocalState && sectionLocalState.section_id) {
                const serverTime = result?.state?.[0]?.remaining_time;
                const localTime = sectionLocalState.state?.[0]?.remaining_time;
                if (!isNil(serverTime) && !isNil(localTime) && localTime < serverTime) {
                    appState = sectionLocalState;
                }
            }
            if (appState && appState.section_id) {
                if (payload.trackAsyncResume === true) {
                    cxAsyncAssessmentResumeAttempted({});
                }
                yield put(storeAppState(appState));
                const startSectionResponse = (yield select(selectStartSectionResponse));
                const sectionTypeInfo = assessmentType(startSectionResponse.assessment_section);
                if (sectionTypeInfo.isAssessmentTypeCoding) {
                    yield put(syncAppState({ app_state: appState }));
                }
                if (payload?.callback?.onSuccess) {
                    yield call(payload?.callback.onSuccess);
                }
            }
        }
        else {
            throw Error('No Section id found');
        }
    }
    catch (error) {
        if (payload.trackAsyncResume === true) {
            cxAsyncAssessmentResumeFailed({});
        }
        yield call(catchError, {
            title: 'getAppStateInstance',
            error: error,
            skipToast: true,
        });
    }
}
export function* fetchWorkflowCandidateStepIdWorker() {
    const testInstanceId = (yield select(selectTestInstanceId));
    if (!testInstanceId) {
        throw new Error('testInstanceId is null');
    }
    try {
        const response = yield call(fetchData, {
            queryString: GET_WORKFLOW_CANDIDATE_STEP_ID,
            queryKey: 'wft_workflow_candidate_step',
            queryVariables: { test_instance_id: testInstanceId },
        });
        const workflowCandidateStepId = response?.[0]?.workflowCandidate?.workflow_candidate_steps[0]?.id;
        if (workflowCandidateStepId) {
            yield put(storeWorkflowCandidateStepId(workflowCandidateStepId));
        }
        return workflowCandidateStepId;
    }
    catch (error) {
        yield call(catchError, {
            title: 'workflowCandidateStep',
            error: error,
        });
    }
}
// The id of the step row that holds the test instance (not the candidate's first step), keyed by the
// test instance in the store so a re-attempt never reuses an earlier id.
export function* lookUpWorkflowCandidateStepIdWorker(action) {
    let workflowCandidateStepId = null;
    try {
        const response = (yield call(fetchData, {
            queryString: GET_WORKFLOW_CANDIDATE_STEP_ID_BY_TEST_INSTANCE,
            queryKey: 'wft_workflow_candidate_step',
            queryVariables: { test_instance_id: action.payload.test_instance_id },
        }));
        workflowCandidateStepId = response?.[0]?.id ?? null;
    }
    catch (error) {
        yield call(catchError, { title: 'lookUpWorkflowCandidateStepId', error: error });
    }
    yield put(storeWorkflowCandidateStepIdLookup(workflowCandidateStepId));
}
export function* updateCandidateStepSyncWorker(action) {
    try {
        const assessmentPreform = action.payload;
        // Hasura rejects the whole mutation on a null Int (NaN serialises to null), so never post
        // without real IDs.
        if (!Number.isInteger(assessmentPreform.step_form_id) || !Number.isInteger(assessmentPreform.form_instance_id)) {
            throw new Error(`Pre-form IDs invalid; step_form_id=${assessmentPreform.step_form_id}, form_instance_id=${assessmentPreform.form_instance_id}`);
        }
        const workflowCandidateStepId = (yield call(fetchWorkflowCandidateStepIdWorker));
        if (!workflowCandidateStepId)
            throw new Error('Step ID not found');
        const payloadWithStepId = {
            ...assessmentPreform,
            id: workflowCandidateStepId,
        };
        const response = yield call(postData, {
            queryString: WFT_UPDATE_WORKFLOW_CANDIDATE_STEP_SYNC,
            payload: payloadWithStepId,
        });
        const mutationResult = response?.data?.wft_update_workflow_candidate_step_sync?.[0];
        if (mutationResult?.success) {
            if (assessmentPreform.callback?.onSuccess) {
                yield call(assessmentPreform.callback.onSuccess);
            }
        }
        else {
            throw Error('CandidateStep Sync Worker Failed');
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'updateCandidateStepSync',
            error: error,
        });
    }
}
export function* triggerTimeUpWorker({ payload, }) {
    try {
        yield put({ type: stopHeartBeat });
        const startSectionResponse = (yield select(selectStartSectionResponse));
        const isCodingSection = Boolean(startSectionResponse && assessmentType(startSectionResponse.assessment_section).isAssessmentTypeCoding);
        // Ask code-solo iframe (if present) to flush the latest in-editor state before redirect.
        yield call(postMessageToChild, { type: 'REQUEST_SYNC_APP_STATE' });
        if (isCodingSection) {
            // Wait for a force-sync acknowledgement from coding iframe, with a timeout fallback.
            const raceResult = (yield race({
                synced: take(codeSoloSyncAppStateFlushed.type),
                timeout: delay(1200),
            }));
            if (raceResult?.timeout) {
                yield call(captureMessage, 'triggerTimeUpWorker: sync timed out', 'warning');
            }
        }
        else {
            // Non-coding sections do not have iframe sync; keep a brief settle window before redirect.
            yield delay(300);
        }
        if (payload?.callback?.onSuccess) {
            yield call(payload.callback.onSuccess, { redirectTo: '/assessment/submit' });
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'triggerTimeUpWorker',
            error: error,
        });
        if (payload?.callback?.onError)
            yield call(payload.callback.onError, error);
    }
}
export function* getIvyReportDetailsWorker({ payload }) {
    const { data, callback } = payload;
    const { resource_id, resource_type_id } = data;
    try {
        const response = yield call(fetchData, {
            queryString: GET_ASSESSMENT_REPORT_DETAILS,
            queryVariables: {
                resource_id,
                resource_type_id,
            },
        });
        const isVisualCuesEnabled = growthBook?.getFeatureValue(growthBookFeatureFlags.CX_IVY_REPORT_ENABLE_VISUAL_CUES, true);
        const isScoringEnabled = growthBook?.getFeatureValue(growthBookFeatureFlags.CX_REPORT_SCORING_ENABLED, true);
        // Scoring flag: disabling it also disables visual cues as well as numerical scoring for this report.
        const result = formatIvyReportDetails(response, isVisualCuesEnabled && isScoringEnabled, isScoringEnabled);
        if (result) {
            yield put(storeIvyReportDetails(result));
            if (callback?.onSuccess) {
                yield call(callback?.onSuccess, result);
            }
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'getIvyReportDetailsWorker',
            error: error,
            skipToast: true,
        });
    }
}
export function* getLanguageSpeakingReportWorker({ payload }) {
    const { data, callback } = payload;
    try {
        const response = yield call(fetchData, {
            queryString: GET_ASSESSMENT_REPORT_DETAILS,
            queryVariables: data,
        });
        const report = formatLanguageEvaluationReport(response);
        yield put(storeLanguageReport(report));
        if (callback?.onSuccess) {
            yield call(callback.onSuccess, report);
        }
    }
    catch (error) {
        yield call(catchError, {
            title: 'getLanguageSpeakingReportWorker',
            error: error,
            skipToast: true,
        });
        if (callback?.onError) {
            yield call(callback.onError, error);
        }
    }
}
export function* getCombinedReportWorker({ payload }) {
    const { data, callback } = payload;
    const { resource_type_id, resource_id } = data;
    if (!resource_type_id || !resource_id) {
        yield put(resetCombinedReport());
        if (callback?.onError)
            yield call(callback.onError, new Error('Missing resource params'));
        return;
    }
    try {
        const response = (yield call(fetchData, {
            queryString: GET_ASSESSMENT_REPORT_DETAILS,
            queryVariables: { resource_type_id, resource_id },
        }));
        const report = normalizeCombinedReportResponse(response.res_find.data.ref);
        yield put(storeCombinedReport(report));
        if (callback?.onSuccess)
            yield call(callback.onSuccess, report);
    }
    catch (error) {
        yield call(catchError, {
            title: 'getCombinedReportWorker',
            error: error,
            skipToast: true,
        });
        yield put(resetCombinedReport());
        if (callback?.onError)
            yield call(callback.onError, error);
    }
}
function* faceAuthStatusUpdateWorker() {
    const preInstructionState = (yield select(selectPreInstructionState));
    yield put(faceAuthStateUpdate({
        isCandidateAuthEnabled: preInstructionState.isCandidateAuthEnabled,
        faceAuthCompleted: true,
    }));
}
function* formSubmissionStatusUpdateWorker({ payload }) {
    if (payload?.formType === 'preAssessment') {
        yield call(localStorageSetItem, ASSESSMENT_PRE_REGISTRATION_FORM, 'true');
        yield put(formSubmissionStateUpdate({
            preformSubmitted: true,
        }));
    }
}
export function* executeCodeRunWorker({ payload, }) {
    const callback = payload?.callback;
    try {
        if (!payload?.input) {
            throw new Error('Please enter the required input before running the code');
        }
        const response = (yield call(postData, {
            queryString: CR_EXECUTE_CODE,
            payload: payload.input,
            spreadPayload: true,
        }));
        const result = response?.data?.cr_execute;
        const executionId = result?.execution_id;
        if (!executionId) {
            throw new Error('Code execution failed');
        }
        yield put(subscribeExecuteCode({ executionId, callback }));
    }
    catch (error) {
        const message = error?.message || 'Code execution failed';
        yield put(executeCodeFailure(message));
        if (callback?.onError) {
            yield call(callback.onError, error);
        }
        yield call(postMessageToChild, { type: 'run_code_complete', success: false });
        yield call(catchError, {
            title: 'executeCodeWorker',
            error: error,
        });
    }
}
export function* subscribeExecuteCodeWorker({ payload }) {
    const { executionId, callback } = payload;
    const channel = createApolloSubscriptionChannel({
        query: SUBSCRIBE_CODE_EXECUTION,
        variables: { execution_id: executionId },
    });
    try {
        while (true) {
            const { data, error } = (yield take(channel));
            if (error) {
                throw error;
            }
            const execution = data?.cr_execution?.[0];
            const status = execution?.status;
            if (!execution) {
                continue;
            }
            if (status && status !== codeRunnerStatus.COMPLETED && status !== codeRunnerStatus.ACCEPTED) {
                throw new Error(`Please enter the required input before running the code`);
            }
            if (status === codeRunnerStatus.COMPLETED) {
                const formattedExecution = formatExecuteCodeSubscriptionOutput(execution);
                yield put(storeExecuteCodeResult(formattedExecution));
                yield call(postMessageToChild, {
                    type: 'run_code_complete',
                    payload: formattedExecution,
                    success: true,
                });
                if (callback?.onSuccess) {
                    yield call(callback.onSuccess, formattedExecution);
                }
                break;
            }
        }
    }
    catch (error) {
        const message = error?.message;
        const fallbackExecution = {
            memory_used: null,
            std_out: null,
            std_err: null,
            cpu_time: null,
            status: 'PENDING',
            id: executionId,
        };
        yield put(executeCodeFailure(message));
        if (callback?.onError) {
            yield call(callback.onError, error);
        }
        yield call(postMessageToChild, {
            type: 'run_code_complete',
            payload: fallbackExecution,
            success: false,
        });
        yield call(catchError, {
            title: 'Code execution failed',
            error: error,
        });
    }
    finally {
        channel.close();
        yield put(resetExecuteCodeLoading());
    }
}
export function* faceAuthSuccessWatcher() {
    yield takeLatest(createFaceAuthSuccess.type, faceAuthStatusUpdateWorker);
}
export function* initializeWatcher() {
    yield debounce(1000, initialize.type, initializeWorker);
}
export function* getStepDetailsWatcher() {
    yield debounce(1000, getStepDetails.type, getStepDetailsWorker);
}
export function* startAssessmentWatcher() {
    yield debounce(1000, startAssessment.type, startAssessmentWorker);
}
export function* startSectionWatcher() {
    yield debounce(1000, startSection.type, startSectionWorker);
}
export function* getNextSectionWatcher() {
    yield takeLatest(submitSection.type, getNextSectionWorker);
}
export function* fetchNextQuestionByIdWatcher() {
    yield takeLatest(fetchNextQuestionById.type, fetchNextQuestionByIdWorker);
}
export function* fetchQuestionsListWatcher() {
    yield takeLatest(fetchQuestionsList.type, fetchQuestionsListWorker);
}
export function* saveAnswerWatcher() {
    yield takeLatest(saveAnswer.type, saveAnswerWorker);
}
export function* executeCodeRunWatcher() {
    yield takeLatest(executeCode.type, executeCodeRunWorker);
}
export function* subscribeExecuteCodeWatcher() {
    yield takeLatest(subscribeExecuteCode.type, subscribeExecuteCodeWorker);
}
export function* stopSectionWatcher() {
    yield takeLeading(stopSection.type, stopSectionWorker);
}
export function* fetchTcfMeasureWatcher() {
    // takeLeading, not debounce: the worker now paces its own retries, so a remount must not restart
    // a poll that is already in flight.
    yield takeLeading(fetchTcfMeasure.type, fetchTcfMeasureWorker);
}
export function* saveAppStateWatcher() {
    yield debounce(2000, saveAppState.type, saveAppStateWorker);
}
export function* syncAppStateWatcher() {
    yield takeLatest(syncAppState.type, syncAppStateWorker);
}
export function* createTestInstanceWatcher() {
    yield takeLatest(fetchTestInstance.type, createTestInstanceWorker);
}
export function* getAppStateWatcher() {
    yield takeLatest(getAppState.type, getAppStateWorker);
}
export function* saveBulkAnswerWatcher() {
    yield debounce(1000, saveBulkAnswer.type, saveBulkAnswerWorker);
}
export function* lookUpWorkflowCandidateStepIdWatcher() {
    yield takeLatest(lookUpWorkflowCandidateStepId.type, lookUpWorkflowCandidateStepIdWorker);
}
export function* updateCandidateStepSyncWatcher() {
    yield takeLatest(updateAssessmentPreformRequest.type, updateCandidateStepSyncWorker);
}
export function* triggerTimeUpWatcher() {
    yield takeLatest(triggerTimeUp.type, triggerTimeUpWorker);
}
export function* getIvyReportDetailsWatcher() {
    yield takeLatest(fetchIvyReportDetails.type, getIvyReportDetailsWorker);
}
export function* getLanguageSpeakingReportWatcher() {
    yield takeLatest(fetchLanguageSpeakingReport.type, getLanguageSpeakingReportWorker);
}
export function* getCombinedReportWatcher() {
    yield takeLatest(fetchCombinedReport.type, getCombinedReportWorker);
}
export function* formSubmissionSuccessWatcher() {
    yield takeLatest('createFormSubmissionSuccess', formSubmissionStatusUpdateWorker);
}
export function* assessmentRootSaga() {
    yield fork(initializeWatcher);
    yield fork(getStepDetailsWatcher);
    yield fork(startAssessmentWatcher);
    yield fork(startSectionWatcher);
    yield fork(fetchNextQuestionByIdWatcher);
    yield fork(fetchQuestionsListWatcher);
    yield fork(saveAnswerWatcher);
    yield fork(stopSectionWatcher);
    yield fork(executeCodeRunWatcher);
    yield fork(subscribeExecuteCodeWatcher);
    yield fork(fetchTcfMeasureWatcher);
    yield fork(saveAppStateWatcher);
    yield fork(syncAppStateWatcher);
    yield fork(createTestInstanceWatcher);
    yield fork(getNextSectionWatcher);
    yield fork(getAppStateWatcher);
    yield fork(saveBulkAnswerWatcher);
    yield fork(proviewSessionInitializedWatcher);
    yield fork(updateCandidateStepSyncWatcher);
    yield fork(lookUpWorkflowCandidateStepIdWatcher);
    yield fork(triggerTimeUpWatcher);
    yield fork(getIvyReportDetailsWatcher);
    yield fork(getLanguageSpeakingReportWatcher);
    yield fork(getCombinedReportWatcher);
    yield fork(faceAuthSuccessWatcher);
    yield fork(formSubmissionSuccessWatcher);
}
export { APP_STATE_PAYLOAD, CANX_GET_APP_STATE, CANX_UPSERT_APP_STATE, localStorageSetItem };
